import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useTextModeration } from "./useTextModeration";

interface PopularTag {
  tag_name: string;
  count: number;
}

interface UserTag {
  tag_name: string;
}

export function useBookTags(bookId: string, userId?: string) {
  const queryClient = useQueryClient();
  const { moderateText } = useTextModeration();

  // Fetch popular tags for this book
  const { data: popularTags = [], isLoading: loadingPopular } = useQuery({
    queryKey: ["book-tags-popular", bookId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("book_tags")
        .select("tag_name")
        .eq("book_id", bookId);

      if (error) throw error;

      // Count occurrences of each tag
      const tagCounts = data.reduce((acc, { tag_name }) => {
        acc[tag_name] = (acc[tag_name] || 0) + 1;
        return acc;
      }, {} as Record<string, number>);

      // Convert to array and sort by count
      return Object.entries(tagCounts)
        .map(([tag_name, count]) => ({ tag_name, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 10);
    },
    enabled: !!bookId,
  });

  // Fetch user's tags for this book
  const { data: userTags = [], isLoading: loadingUser } = useQuery({
    queryKey: ["book-tags-user", bookId, userId],
    queryFn: async () => {
      if (!userId) return [];

      const { data, error } = await supabase
        .from("book_tags")
        .select("tag_name")
        .eq("book_id", bookId)
        .eq("user_id", userId);

      if (error) throw error;
      return data;
    },
    enabled: !!bookId && !!userId,
  });

  // Add tag mutation
  const addTag = useMutation({
    mutationFn: async (tagName: string) => {
      if (!userId) throw new Error("Must be logged in");

      const normalizedTag = tagName.toLowerCase().trim();

      // Moderate tag content before adding
      const isAllowed = await moderateText(normalizedTag);
      if (!isAllowed) {
        throw new Error("Tag contains inappropriate content");
      }

      // Insert into book_tags
      const { error: tagError } = await supabase
        .from("book_tags")
        .insert({ book_id: bookId, tag_name: normalizedTag, user_id: userId });

      if (tagError && !tagError.message.includes("duplicate")) throw tagError;

      // Update user_books.tags array
      const { data: userBook } = await supabase
        .from("user_books")
        .select("tags")
        .eq("book_id", bookId)
        .eq("user_id", userId)
        .single();

      const existingTags = userBook?.tags || [];
      if (!existingTags.includes(normalizedTag)) {
        const { error: updateError } = await supabase
          .from("user_books")
          .update({ tags: [...existingTags, normalizedTag] })
          .eq("book_id", bookId)
          .eq("user_id", userId);

        if (updateError) throw updateError;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["book-tags-popular", bookId] });
      queryClient.invalidateQueries({ queryKey: ["book-tags-user", bookId, userId] });
      queryClient.invalidateQueries({ queryKey: ["user-tag-history", userId] });
      toast.success("Tag added");
    },
    onError: (error) => {
      console.error("Error adding tag:", error);
      toast.error("Failed to add tag");
    },
  });

  // Remove tag mutation
  const removeTag = useMutation({
    mutationFn: async (tagName: string) => {
      if (!userId) throw new Error("Must be logged in");

      const normalizedTag = tagName.toLowerCase().trim();

      // Delete from book_tags
      const { error: deleteError } = await supabase
        .from("book_tags")
        .delete()
        .eq("book_id", bookId)
        .eq("tag_name", normalizedTag)
        .eq("user_id", userId);

      if (deleteError) throw deleteError;

      // Update user_books.tags array
      const { data: userBook } = await supabase
        .from("user_books")
        .select("tags")
        .eq("book_id", bookId)
        .eq("user_id", userId)
        .single();

      const existingTags = userBook?.tags || [];
      const { error: updateError } = await supabase
        .from("user_books")
        .update({ tags: existingTags.filter((t) => t !== normalizedTag) })
        .eq("book_id", bookId)
        .eq("user_id", userId);

      if (updateError) throw updateError;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["book-tags-popular", bookId] });
      queryClient.invalidateQueries({ queryKey: ["book-tags-user", bookId, userId] });
      toast.success("Tag removed");
    },
    onError: (error) => {
      console.error("Error removing tag:", error);
      toast.error("Failed to remove tag");
    },
  });

  return {
    popularTags,
    userTags: userTags.map((t) => t.tag_name),
    isLoading: loadingPopular || loadingUser,
    addTag: addTag.mutate,
    removeTag: removeTag.mutate,
    isAddingTag: addTag.isPending,
    isRemovingTag: removeTag.isPending,
  };
}
